import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { ViewAsClientProvider } from "@/lib/viewAsClient";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/AppLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Sites from "@/pages/Sites";
import SiteDetail from "@/pages/SiteDetail";
import Bookings from "@/pages/Bookings";
import DockManager from "@/pages/DockManager";
import RecurringPatterns from "@/pages/RecurringPatterns";
import Blackouts from "@/pages/Blackouts";
import EmailSettings from "@/pages/EmailSettings";
import EmailLog from "@/pages/EmailLog";
import Broadcasts from "@/pages/Broadcasts";
import AdminBranding from "@/pages/AdminBranding";
import AdminReservationTypes from "@/pages/AdminReservationTypes";
import AdminFormDesigner from "@/pages/AdminFormDesigner";
import AdminDriverDeclarations from "@/pages/AdminDriverDeclarations";
import AdminEmailHandling from "@/pages/AdminEmailHandling";
import AdminFieldCatalogue from "@/pages/AdminFieldCatalogue";
import AdminOnboardingRequests from "@/pages/AdminOnboardingRequests";
import AdminArchive from "@/pages/AdminArchive";
import AdminImportArchive from "@/pages/AdminImportArchive";
import AdminBookingEdit from "@/pages/AdminBookingEdit";
import AdminOverrideRequests from "@/pages/AdminOverrideRequests";
import AdminImportUsers from "@/pages/AdminImportUsers";
import AdminImportBookings from "@/pages/AdminImportBookings";
import AdminImportPicklists from "@/pages/AdminImportPicklists";
import AdminTermsOfService from "@/pages/AdminTermsOfService";
import Users from "@/pages/Users";
import DriverDashboard from "@/pages/DriverDashboard";
import PublicDriverDashboard from "@/pages/PublicDriverDashboard";
import PublicBooking from "@/pages/PublicBooking";
import PublicLinks from "@/pages/PublicLinks";
import ClientSettings from "@/pages/ClientSettings";
import Picklists from "@/pages/Picklists";
import SiteRulesEdit from "@/pages/SiteRulesEdit";
import SiteRulesPublic from "@/pages/SiteRulesPublic";
import BookingChecklist from "@/pages/BookingChecklist";
import SamplingRules from "@/pages/SamplingRules";
import SamplingQueue from "@/pages/SamplingQueue";
import PalletReconciliation from "@/pages/PalletReconciliation";
import EmailTemplates from "@/pages/EmailTemplates";
import IssuesInbox from "@/pages/IssuesInbox";
import ApprovedBookers from "@/pages/ApprovedBookers";
import AdminImportApprovedBookers from "@/pages/AdminImportApprovedBookers";
import NewBookingPicker from "@/pages/NewBookingPicker";
import NewBookingDocs from "@/pages/NewBookingDocs";
import NewBookingEdit from "@/pages/NewBookingEdit";
import PublicSiteInfo from "@/pages/PublicSiteInfo";
import DriverSiteInfo from "@/pages/DriverSiteInfo";
import AdminDrivers from "@/pages/AdminDrivers";
import MyDrivers from "@/pages/MyDrivers";
import PublicNcr from "@/pages/PublicNcr";
import Reports from "@/pages/Reports";
import ReportRunner from "@/pages/ReportRunner";
import CustomReportBuilder from "@/pages/CustomReportBuilder";
import PalletHireDehire from "@/pages/PalletHireDehire";
import BookerDashboard from "@/pages/BookerDashboard";
import BookingPrint from "@/pages/BookingPrint";
import HelpBookingFlow from "@/pages/HelpBookingFlow";
import UserManual from "@/pages/UserManual";
import UserManualPrint from "@/pages/UserManualPrint";
import KcsOrSuperAdminOnly from "@/components/KcsOrSuperAdminOnly";
import SiteManager from "@/pages/SiteManager";
import BayView from "@/pages/BayView";
import Forbidden from "@/pages/Forbidden";
import NotFound from "@/pages/NotFound";
import "@/App.css";

// Placeholder screens for routes that will be built in later phases.
// Each is wrapped in ProtectedRoute via the route definitions below.
const PlaceholderPage = ({ title, testid }) => (
  <div data-testid={testid} className="space-y-2">
    <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
    <p className="text-sm text-slate-500">Coming in the next phase.</p>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <ViewAsClientProvider>
      <BrowserRouter>
        <Routes>
          {/* Public auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/driver/:token" element={<PublicDriverDashboard />} />

          {/* Standalone printable pages — auth required but no AppLayout chrome
             so the print output stays clean. */}
          <Route
            path="/bookings/:id/print"
            element={
              <ProtectedRoute>
                <BookingPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/user-manual/print"
            element={
              <ProtectedRoute>
                <KcsOrSuperAdminOnly>
                  <UserManualPrint />
                </KcsOrSuperAdminOnly>
              </ProtectedRoute>
            }
          />

          {/* Authenticated routes */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route
              path="/dock"
              element={
                <ProtectedRoute
                  roles={[
                    "super_admin",
                    "client_admin",
                    "site_admin",
                    "dock_manager",
                    "receiving",
                    "dispatch",
                    "gate",
                  ]}
                >
                  <DockManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings"
              element={<Bookings />}
            />
            <Route
              path="/help/booking-flow"
              element={
                <ProtectedRoute>
                  <HelpBookingFlow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help/user-manual"
              element={
                <ProtectedRoute>
                  <KcsOrSuperAdminOnly>
                    <UserManual />
                  </KcsOrSuperAdminOnly>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sites"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <Sites />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sites/:id"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <SiteDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/recurring"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager"]}>
                  <RecurringPatterns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blackouts"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager"]}>
                  <Blackouts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/email"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <EmailSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/email-log"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <EmailLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/broadcasts"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <Broadcasts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/override-requests"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager"]}>
                  <AdminOverrideRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/branding"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <AdminBranding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reservation-types"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <AdminReservationTypes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/terms-of-service"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <AdminTermsOfService />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <Navigate to="/site-manager" replace />
                </ProtectedRoute>
              }
            />
            {/* /broker route removed (P2-h, Feb 2026) — BrokerDashboard
                superseded by BookerDashboard for the `approved_booker` role.
                Anyone with the legacy `broker` role now lands on /booker. */}
            <Route
              path="/broker"
              element={<Navigate to="/booker" replace />}
            />
            <Route
              path="/driver"
              element={
                <ProtectedRoute roles={["driver", "super_admin", "client_admin"]}>
                  <DriverDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/public-links"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <PublicLinks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <ClientSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/picklists"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <Picklists />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/driver-declarations"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <AdminDriverDeclarations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/form-designer"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <AdminFormDesigner />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/field-catalogue"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <AdminFieldCatalogue />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/onboarding"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <AdminOnboardingRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/archive"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <AdminArchive />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/import/archive"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <AdminImportArchive />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/email-handling"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <AdminEmailHandling />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/booking-edit"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <AdminBookingEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/import/approved-bookers"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <AdminImportApprovedBookers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/import/users"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <AdminImportUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/import/bookings"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <AdminImportBookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/import/picklists"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <AdminImportPicklists />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sampling-rules"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <SamplingRules />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sampling-queue"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager"]}>
                  <SamplingQueue />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pallets"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager"]}>
                  <PalletReconciliation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pallets/hire"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager"]}>
                  <PalletHireDehire />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports/custom"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <CustomReportBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports/:reportKey"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <ReportRunner />
                </ProtectedRoute>
              }
            />
            <Route
              path="/booker"
              element={
                <ProtectedRoute roles={["approved_booker", "super_admin", "client_admin"]}>
                  <BookerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/site-manager"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager"]}>
                  <SiteManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bay-view"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager", "receiving", "dispatch", "gate"]}>
                  <BayView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/email-templates"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <EmailTemplates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/approved-bookers"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin"]}>
                  <ApprovedBookers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings/new"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager", "dispatch", "receiving", "approved_booker"]}>
                  <NewBookingPicker />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings/new/docs"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager", "dispatch", "receiving", "approved_booker"]}>
                  <NewBookingDocs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings/new/edit"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager", "dispatch", "receiving", "approved_booker"]}>
                  <NewBookingEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/drivers"
              element={
                <ProtectedRoute roles={["approved_booker"]}>
                  <MyDrivers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/drivers"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager", "receiving", "dispatch", "gate"]}>
                  <AdminDrivers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/issues"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin", "dock_manager"]}>
                  <IssuesInbox />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sites/:id/rules"
              element={
                <ProtectedRoute roles={["super_admin", "client_admin", "site_admin"]}>
                  <SiteRulesEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dock/checklist/:bookingId"
              element={<BookingChecklist />}
            />
            <Route
              path="/profile"
              element={<PlaceholderPage title="Profile" testid="profile-page" />}
            />
            <Route path="/forbidden" element={<Forbidden />} />
          </Route>

          {/* Public booking link (path-based, anonymous) */}
          <Route path="/book/:slug" element={<PublicBooking />} />
          {/* Public site rules page (anonymous — driver-friendly link) */}
          <Route path="/site/:siteId/rules" element={<SiteRulesPublic />} />
          {/* Phase E: combined public site-info page linked from booking emails */}
          <Route path="/p/:clientSlug/site-info/:siteId" element={<PublicSiteInfo />} />
          {/* Phase β: anonymous NCR resolution page (token-gated) */}
          <Route path="/p/ncr/:token" element={<PublicNcr />} />
          {/* Phase F δ: per-booking integrated driver site-info (noindex, token-gated) */}
          <Route path="/d/:token" element={<DriverSiteInfo />} />

          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
        <Toaster />
      </BrowserRouter>
      </ViewAsClientProvider>
    </AuthProvider>
  );
}

export default App;
